import { BaseClient } from './baseClient';

/**
 * The signed-in user's notification inbox (the bell in the top bar). All four
 * endpoints are scoped server-side to the caller's own notifications — no id
 * of another user's notification resolves, so the client never filters.
 *
 * PRIVACY: a notification names the supplier (`supplierName`) and never a
 * contact address — supplier email addresses do not reach the browser.
 */

/**
 * `RESEARCH_READY` / `RESEARCH_PROBLEM`: a supplier-research round on the RFQ
 * landed (with suppliers for at least one component, or for none). They carry
 * no threadId; the deep link is the RFQ's discovery step.
 */
export type NotificationType = 'SUPPLIER_REPLY' | 'RESEARCH_READY' | 'RESEARCH_PROBLEM';

export interface NotificationItem {
    id: string;
    type: NotificationType;
    title: string;
    /** Short honest summary; never carries an email address. */
    body: string;
    /** Human RFQ id (`RFQ-039`) — the one wizard URLs use. */
    rfqId: string;
    rfqName?: string;
    /** Outreach conversation thread — deep-link target when present. */
    threadId?: string;
    componentId?: string;
    componentName?: string;
    supplierName?: string;
    /** ISO timestamp once read; absent while unread. */
    readAt?: string;
    createdAt: string;
}

export interface NotificationList {
    items: NotificationItem[];
    unreadCount: number;
}

interface Envelope<T> {
    success: boolean;
    data: T;
}

class NotificationsClient extends BaseClient {
    /** Newest notifications plus the unread count, in one response. */
    async list(params: { limit?: number; unreadOnly?: boolean } = {}): Promise<NotificationList> {
        const response = await this.api.get<Envelope<NotificationList>>('/trp/notifications', {
            params: {
                ...(params.limit !== undefined ? { limit: params.limit } : {}),
                ...(params.unreadOnly ? { unreadOnly: 'true' } : {}),
            },
        });
        return response.data.data;
    }

    /** Just the badge number — cheap enough to poll. */
    async unreadCount(): Promise<number> {
        const response = await this.api.get<Envelope<{ unreadCount: number }>>(
            '/trp/notifications/unread-count'
        );
        return response.data.data.unreadCount;
    }

    /** Idempotent; 404s for anything that is not the caller's. */
    async markRead(id: string): Promise<NotificationItem> {
        const response = await this.api.post<Envelope<NotificationItem>>(
            `/trp/notifications/${encodeURIComponent(id)}/read`
        );
        return response.data.data;
    }

    /** Marks every unread notification read; returns how many changed. */
    async markAllRead(): Promise<number> {
        const response = await this.api.post<Envelope<{ modified: number }>>(
            '/trp/notifications/read-all'
        );
        return response.data.data.modified;
    }
}

export const notificationsClient = new NotificationsClient();
